export type ConsentState = {
  analytics: boolean
  marketing: boolean
}

type StoredConsent = ConsentState & { v: number }

export const CONSENT_COOKIE = "stz_consent"
export const OPEN_PREFERENCES_EVENT = "stz:open-cookie-preferences"

const CONSENT_VERSION = 1
const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 365

const TRACKER_COOKIE_PATTERNS = {
  analytics: [/^_ga/, /^_gid$/, /^_gat/],
  marketing: [
    /^_fbp$/,
    /^_fbc$/,
    /^_pin_unauth$/,
    /^_pinterest_/,
    /^_ttp$/,
    /^_tt_enable_cookie$/,
    /^__kla_id$/,
    /^hubspotutk$/,
    /^__hs/,
    /^_gcl_/,
    /^_uet/,
  ],
}

type Listener = (consent: ConsentState) => void
const listeners = new Set<Listener>()

const isBrowser = () => typeof window !== "undefined"

export const hasGlobalPrivacyControl = (): boolean =>
  // @ts-ignore
  isBrowser() && navigator.globalPrivacyControl === true

const readCookie = (name: string): string | null => {
  if (!isBrowser()) return null
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`))
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null
}

const readStoredConsent = (): StoredConsent | null => {
  const raw = readCookie(CONSENT_COOKIE)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    if (parsed?.v !== CONSENT_VERSION) return null
    return {
      v: parsed.v,
      analytics: parsed.analytics !== false,
      marketing: parsed.marketing !== false,
    }
  } catch (e) {
    return null
  }
}

export const hasStoredConsent = (): boolean => readStoredConsent() !== null

export const getConsent = (): ConsentState => {
  const stored = readStoredConsent()
  const consent = stored
    ? { analytics: stored.analytics, marketing: stored.marketing }
    : { analytics: true, marketing: true }
  if (hasGlobalPrivacyControl()) consent.marketing = false
  return consent
}

export const toGoogleConsent = ({ analytics, marketing }: ConsentState) => {
  const ads = marketing ? "granted" : "denied"
  return {
    analytics_storage: analytics ? "granted" : "denied",
    ad_storage: ads,
    ad_user_data: ads,
    ad_personalization: ads,
  }
}

const domainVariants = (): (string | undefined)[] => {
  const parts = window.location.hostname.split(".")
  const variants: (string | undefined)[] = [undefined]
  for (let i = 0; i < parts.length - 1; i++) {
    variants.push(`.${parts.slice(i).join(".")}`)
  }
  return variants
}

const deleteCookie = (name: string) => {
  domainVariants().forEach((domain) => {
    document.cookie = `${name}=; Max-Age=0; path=/${
      domain ? `; domain=${domain}` : ""
    }`
  })
}

const clearTrackerCookies = (consent: ConsentState) => {
  const patterns = [
    ...(consent.analytics ? [] : TRACKER_COOKIE_PATTERNS.analytics),
    ...(consent.marketing ? [] : TRACKER_COOKIE_PATTERNS.marketing),
  ]
  if (!patterns.length) return
  document.cookie
    .split("; ")
    .map((row) => row.split("=")[0])
    .filter((name) => patterns.some((pattern) => pattern.test(name)))
    .forEach(deleteCookie)
}

const updateGoogleConsent = (consent: ConsentState) => {
  // @ts-ignore
  if (typeof window.gtag === "function") {
    // @ts-ignore
    window.gtag("consent", "update", toGoogleConsent(consent))
  }
}

export const setConsent = (next: ConsentState) => {
  if (!isBrowser()) return
  const previous = getConsent()
  const consent = {
    analytics: next.analytics,
    marketing: next.marketing && !hasGlobalPrivacyControl(),
  }
  const value = encodeURIComponent(
    JSON.stringify({ v: CONSENT_VERSION, ...consent })
  )
  const secure = window.location.protocol === "https:" ? "; Secure" : ""
  document.cookie = `${CONSENT_COOKIE}=${value}; Max-Age=${CONSENT_MAX_AGE_SECONDS}; path=/; SameSite=Lax${secure}`

  updateGoogleConsent(consent)
  clearTrackerCookies(consent)
  listeners.forEach((listener) => listener(consent))

  // Pixels that already ran keep tracking until the page is unloaded.
  const revoked =
    (previous.analytics && !consent.analytics) ||
    (previous.marketing && !consent.marketing)
  if (revoked) window.location.reload()
}

export const subscribeToConsent = (listener: Listener) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export type PreferencesFocus = "analytics" | "marketing"

export const openCookiePreferences = (focus?: PreferencesFocus) => {
  if (isBrowser()) {
    window.dispatchEvent(
      new CustomEvent(OPEN_PREFERENCES_EVENT, { detail: { focus } })
    )
  }
}

/**
 * Runs inline in <head> before gtag/GTM load, so it can't import anything.
 */
export const consentDefaultScript = `
(function () {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
  var consent = { analytics: true, marketing: true };
  try {
    var match = document.cookie.split("; ").find(function (row) {
      return row.indexOf("${CONSENT_COOKIE}=") === 0;
    });
    if (match) {
      var stored = JSON.parse(decodeURIComponent(match.slice(${
        CONSENT_COOKIE.length + 1
      })));
      if (stored && stored.v === ${CONSENT_VERSION}) {
        consent.analytics = stored.analytics !== false;
        consent.marketing = stored.marketing !== false;
      }
    }
  } catch (e) {}
  if (navigator.globalPrivacyControl === true) consent.marketing = false;
  var ads = consent.marketing ? "granted" : "denied";
  window.gtag("consent", "default", {
    analytics_storage: consent.analytics ? "granted" : "denied",
    ad_storage: ads,
    ad_user_data: ads,
    ad_personalization: ads,
    functionality_storage: "granted",
    security_storage: "granted",
    wait_for_update: 500
  });
  window.gtag("set", "ads_data_redaction", true);
})();
`
